import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {db} from '../firebase-config';
import {Alert, Box, CircularProgress, Container} from '@mui/material';
import {collection, doc, getDoc, getDocs, onSnapshot, serverTimestamp, updateDoc} from 'firebase/firestore';
import Lobby from '../components/lobby/Lobby';
import QuizSelectDialog from '../components/lobby/dialogs/QuizSelectDialog';
import EmojiPickerDialog from '../components/lobby/dialogs/EmojiPickerDialog';
import GifPickerDialog from '../components/lobby/dialogs/GifPickerDialog';
import useChatMessages from '../hooks/useChatMessages';
import useGiphy from '../hooks/useGiphy';
import {leaveCurrentLobby} from '../utils/lobbyUtils';

export default function QuizLobby() {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [gameSession, setGameSession] = useState(null);
  const [isGameMaster, setIsGameMaster] = useState(false);
  const [gameMasterParticipates, setGameMasterParticipates] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizSelectOpen, setQuizSelectOpen] = useState(false);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [giphyAnchorEl, setGiphyAnchorEl] = useState(null);

  const {
    messages,
    chatMessage,
    setChatMessage,
    sendMessage,
    sendGif,
    error: chatError
  } = useChatMessages(lobbyId, currentUser);

  const {
    searchTerm,
    setSearchTerm,
    isLoading: isGiphyLoading,
    error: giphyError,
    fetchGifs
  } = useGiphy();

  const handleEmojiClick = (emoji) => {
    setChatMessage((prev) => prev + emoji.emoji);
    setAnchorEl(null);
  };

  // Überprüfen ob Benutzer eingeloggt ist
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: `/lobby/${lobbyId}`,
          message: 'Bitte melde dich an, um der Lobby beizutreten.' 
        } 
      });
    }
  }, [currentUser, navigate, lobbyId]);

  useEffect(() => {
    if (!lobbyId || !currentUser) return;

    const gameSessionRef = doc(db, 'game_sessions', lobbyId);
    
    const loadInitialData = async () => {
      try {
        const sessionDoc = await getDoc(gameSessionRef);
        if (!sessionDoc.exists()) {
          setError('Lobby nicht gefunden');
          return;
        }

        const sessionData = sessionDoc.data();
        setIsGameMaster(sessionData.gameMasterId === currentUser.uid);
        setGameSession(sessionData);
        
        // Setze den Game Master Teilnahme-Status
        if (sessionData.gameMasterParticipates !== undefined) {
          setGameMasterParticipates(sessionData.gameMasterParticipates);
        }
        
        // Spielerdaten aktualisieren/hinzufügen
        const playerDisplayName = currentUser.displayName;
        if (!playerDisplayName) {
          console.warn('Kein Benutzername gefunden für User:', currentUser.uid);
        }
        
        await updateDoc(gameSessionRef, {
          [`players.${currentUser.uid}`]: {
            displayName: playerDisplayName || 'Unbekannter Spieler',
            photoURL: currentUser.photoURL,
            hasRpmAvatar: currentUser.hasRpmAvatar || false,
            rpmModelUrl: currentUser.rpmModelUrl || null,
            rpmAvatarConfig: currentUser.rpmAvatarConfig || null,
            rpmAvatarType: currentUser.rpmAvatarType || null,
            isReady: false,
            score: 0,
            answers: [],
            correctAnswers: 0,
            isGameMaster: sessionData.gameMasterId === currentUser.uid,
            lastActive: serverTimestamp(),
            ...(currentUser.profilePath && { profilePath: currentUser.profilePath })
          },
          lastActivity: serverTimestamp()
        });

        // Verlasse alle anderen Lobbys, aber nicht die aktuelle
        await leaveCurrentLobby(currentUser.uid, lobbyId);

        // Wenn ein Quiz ausgewählt ist, lade die Quiz-Daten
        if (sessionData.currentQuiz?.quizId) {
          const quizDoc = await getDoc(doc(db, 'quizzes', sessionData.currentQuiz.quizId));
          if (quizDoc.exists()) {
            setSelectedQuiz({ id: quizDoc.id, ...quizDoc.data() });
          }
        }

        // Verfügbare Quizze laden
        const quizzesSnapshot = await getDocs(collection(db, 'quizzes'));
        const quizzes = [];
        quizzesSnapshot.forEach((doc) => {
          quizzes.push({ id: doc.id, ...doc.data() });
        });
        setAvailableQuizzes(quizzes);
        setLoading(false);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Fehler beim Laden der Lobby-Daten');
        setLoading(false);
      }
    };

    loadInitialData();

    // Live Updates für die Session
    const unsubscribeSession = onSnapshot(gameSessionRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const sessionData = docSnapshot.data();
        setGameSession(sessionData);
        setIsGameMaster(sessionData.gameMasterId === currentUser.uid);
        
        // Aktualisiere den Game Master Teilnahme-Status
        if (sessionData.gameMasterParticipates !== undefined) {
          setGameMasterParticipates(sessionData.gameMasterParticipates);
        }
        
        // Lade Quiz-Daten, wenn ein Quiz ausgewählt ist
        if (sessionData.currentQuiz?.quizId) {
          try {
            const quizRef = doc(db, 'quizzes', sessionData.currentQuiz.quizId);
            const quizDoc = await getDoc(quizRef);
            if (quizDoc.exists()) {
              const quizData = { id: quizDoc.id, ...quizDoc.data() };
              setSelectedQuiz(quizData);
            }
          } catch (error) {
            console.error('Error loading quiz data:', error);
          }
        } else {
          setSelectedQuiz(null);
        }
        
        if (sessionData.status === 'playing') {
          navigate(`/game/${lobbyId}`);
        }
      }
    });

    return () => {
      unsubscribeSession();
    };
  }, [lobbyId, currentUser, navigate]);

  // Bereit-Status umschalten
  const toggleReady = async () => {
    try {
      const playerRef = doc(db, 'game_sessions', lobbyId);
      const currentPlayerData = gameSession?.players[currentUser.uid];
      
      await updateDoc(playerRef, {
        [`players.${currentUser.uid}.isReady`]: !currentPlayerData?.isReady,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling ready state:', error);
      setError('Fehler beim Ändern des Bereit-Status');
    }
  };

  // Quiz auswählen
  const selectQuiz = async (quiz) => {
    try {
      if (!quiz?.id) {
        console.error('Ungültige Quiz-Daten:', quiz);
        setError('Ungültige Quiz-Daten');
        return;
      }

      const sessionRef = doc(db, 'game_sessions', lobbyId);
      
      // Lade die vollständigen Quiz-Daten
      const quizDoc = await getDoc(doc(db, 'quizzes', quiz.id));
      if (!quizDoc.exists()) {
        console.error('Quiz nicht gefunden:', quiz.id);
        setError('Quiz nicht gefunden');
        return;
      }
      const quizData = quizDoc.data();
      
      // Überprüfe, ob alle erforderlichen Felder vorhanden sind
      if (!quizData?.title || !quizData?.description) {
        console.error('Unvollständige Quiz-Daten:', quizData);
        setError('Unvollständige Quiz-Daten');
        return;
      }

      // Setze alle Spieler auf "nicht bereit" zurück
      const sessionDoc = await getDoc(sessionRef);
      const currentPlayers = sessionDoc.data()?.players || {};
      const updatedPlayers = {};
      
      Object.entries(currentPlayers).forEach(([playerId, playerData]) => {
        updatedPlayers[playerId] = {
          ...playerData,
          isReady: false,
          score: 0,
          answers: [],
          correctAnswers: 0
        };
      });

      // Erstelle das Quiz-Update-Objekt
      const quizUpdate = {
        quizId: quiz.id,
        currentQuestion: 0,
        startedAt: null,
        endedAt: null,
        title: quizData.title,
        description: quizData.description
      };

      // Aktualisiere die Session mit dem neuen Quiz und zurückgesetzten Spielern
      await updateDoc(sessionRef, {
        currentQuiz: quizUpdate,
        status: 'selecting_quiz',
        players: updatedPlayers,
        lastActivity: serverTimestamp()
      });

      setSelectedQuiz({...quiz, ...quizData});
      setQuizSelectOpen(false);
    } catch (error) {
      console.error('Error selecting quiz:', error);
      setError('Fehler beim Auswählen des Quiz: ' + error.message);
    }
  };

  // Spiel starten
  const startGame = async () => {
    if (!selectedQuiz) {
      setError('Bitte wählen Sie zuerst ein Quiz aus');
      return;
    }

    try {
      // Wenn der Game Master nicht teilnimmt, muss mindestens ein weiterer Spieler in der Lobby sein
      if (!gameMasterParticipates) {
        const activePlayers = Object.keys(gameSession?.players || {}).filter(
          playerId => playerId !== gameSession?.gameMasterId
        );
        
        if (activePlayers.length === 0) {
          setError('Es muss mindestens ein Spieler neben dem Presenter in der Lobby sein');
          return;
        }
      }

      const sessionRef = doc(db, 'game_sessions', lobbyId);
      await updateDoc(sessionRef, {
        status: 'playing',
        'currentQuiz.startedAt': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error starting game:', error);
      setError('Fehler beim Spielstart');
    }
  };

  // Lobby verlassen
  const leaveLobby = async () => {
    try {
      const sessionRef = doc(db, 'game_sessions', lobbyId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        navigate('/');
        return;
      }

      const sessionData = sessionDoc.data();
      const players = { ...sessionData.players };
      delete players[currentUser.uid];

      // Wenn keine Spieler mehr übrig sind, setze den Status auf 'finished'
      if (Object.keys(players).length === 0) {
        await updateDoc(sessionRef, {
          players,
          status: 'finished',
          lastActivity: serverTimestamp()
        });
      } else {
        // Wenn der Game Master geht, übertrage die Rolle an einen anderen Spieler
        let updates = { 
          players,
          lastActivity: serverTimestamp()
        };
        if (sessionData.gameMasterId === currentUser.uid) {
          const newGameMaster = Object.keys(players)[0];
          updates = {
            ...updates,
            gameMasterId: newGameMaster,
            [`players.${newGameMaster}.isGameMaster`]: true
          };
        }
        await updateDoc(sessionRef, updates);
      }

      navigate('/');
    } catch (error) {
      console.error('Error leaving lobby:', error);
      setError('Fehler beim Verlassen der Lobby');
    }
  };

  // Game Master Teilnahme umschalten
  const toggleGameMasterParticipation = async () => {
    if (!isGameMaster) return;
    
    try {
      const sessionRef = doc(db, 'game_sessions', lobbyId);
      await updateDoc(sessionRef, {
        gameMasterParticipates: !gameMasterParticipates,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling game master participation:', error);
      setError('Fehler beim Ändern des Game Master Teilnahme-Status');
    }
  };

  if (!currentUser) return null;

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || chatError) {
    return (
      <Alert severity="error">
        {error || chatError}
      </Alert>
    );
  }

  const allPlayersReady = Object.values(gameSession?.players || {}).every(p => p.isReady);
  const currentPlayerReady = gameSession?.players[currentUser?.uid]?.isReady;

  return (
    <Container maxWidth="xl">
      <Lobby
        lobbyId={lobbyId}
        selectedQuiz={selectedQuiz}
        isGameMaster={isGameMaster}
        gameMasterParticipates={gameMasterParticipates}
        onToggleGameMasterParticipation={toggleGameMasterParticipation}
        currentPlayerReady={currentPlayerReady}
        allPlayersReady={allPlayersReady}
        players={gameSession?.players || {}}
        onToggleReady={toggleReady}
        onQuizSelect={() => setQuizSelectOpen(true)}
        onStartGame={startGame}
        onLeaveLobby={leaveLobby}
        messages={messages}
        chatMessage={chatMessage}
        onMessageChange={(e) => setChatMessage(e.target.value)}
        onMessageSubmit={sendMessage}
        onEmojiClick={handleEmojiClick}
        onGifClick={sendGif}
        currentUser={currentUser}
        anchorEl={anchorEl}
        giphyAnchorEl={giphyAnchorEl}
        onEmojiButtonClick={(e) => setAnchorEl(e.currentTarget)}
        onGifButtonClick={(e) => setGiphyAnchorEl(e.currentTarget)}
      />

      <QuizSelectDialog
        open={quizSelectOpen}
        onClose={() => setQuizSelectOpen(false)}
        quizzes={availableQuizzes}
        onQuizSelect={selectQuiz}
        selectedQuizId={selectedQuiz?.id}
      />

      <EmojiPickerDialog
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onEmojiSelect={handleEmojiClick}
      />

      <GifPickerDialog
        anchorEl={giphyAnchorEl}
        onClose={() => {
          setGiphyAnchorEl(null);
          setSearchTerm('');
        }}
        onGifSelect={sendGif}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        gifs={[]}
        isLoading={isGiphyLoading}
        error={giphyError}
        onSearch={fetchGifs}
      />
    </Container>
  );
} 